import type {ReactElement} from 'react';

import {NumberInput, Select} from '@/components/common/Input';

export interface LimitInputRowProps<
  TType extends string,
  TFrequency extends string,
  TBound extends string = string,
> {
  label?: string;
  type: TType;
  value?: number | string;
  frequency: TFrequency;
  typeOptions: {key: string; value: TType}[];
  frequencyOptions: {key: string; value: TFrequency}[];
  onTypeChange: (type: TType) => void;
  onValueChange: (value: number) => void;
  onFrequencyChange: (frequency: TFrequency) => void;
  bound?: TBound;
  boundOptions?: {key: string; value: TBound}[];
  onBoundChange?: (bound: TBound) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  stacked?: boolean;
  typeLabel?: string;
  boundLabel?: string;
  valueLabel?: string;
  frequencyLabel?: string;
  typeSelectClassName?: string;
  boundSelectClassName?: string;
  valueInputClassName?: string;
  frequencySelectClassName?: string;
  /** Test hook prefix: `-type`, `-value` and `-frequency` mark the three controls. */
  dataCy?: string;
}

const LimitInputRow = <
  TType extends string,
  TFrequency extends string,
  TBound extends string,
>({
  label,
  type,
  value,
  frequency,
  typeOptions,
  frequencyOptions,
  onTypeChange,
  onValueChange,
  onFrequencyChange,
  bound,
  boundOptions,
  onBoundChange,
  min,
  max,
  placeholder = '0',
  stacked = false,
  typeLabel,
  boundLabel,
  valueLabel,
  frequencyLabel,
  typeSelectClassName,
  boundSelectClassName,
  valueInputClassName,
  frequencySelectClassName,
  dataCy,
}: LimitInputRowProps<TType, TFrequency, TBound>): ReactElement => {
  const stackedTypeSelectClassName =
    typeSelectClassName ??
    'w-full rounded-md border bg-transparent focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
  const stackedBoundSelectClassName =
    boundSelectClassName ??
    'w-full rounded-md border bg-transparent focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
  const stackedValueInputClassName =
    valueInputClassName ??
    'w-full rounded-md border text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
  const stackedFrequencySelectClassName =
    frequencySelectClassName ??
    'w-full rounded-md border bg-transparent focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  const rowTypeSelectClassName =
    typeSelectClassName ??
    'border-r-0 rounded-r-none bg-transparent min-w-[65px] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
  const rowBoundSelectClassName =
    boundSelectClassName ??
    'border-r-0 rounded-r-none bg-transparent min-w-[65px] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
  const rowValueInputClassName =
    valueInputClassName ??
    'flex-1 min-w-0 w-full text-center rounded-none border-x-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
  const rowFrequencySelectClassName =
    frequencySelectClassName ??
    'border-l-0 rounded-l-none bg-transparent min-w-[80px] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  const wrapperClassName = stacked
    ? 'relative mt-1 rounded-md shadow-sm flex flex-col gap-2'
    : 'relative mt-1 rounded-md shadow-sm flex';

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
        </label>
      )}
      <div className={wrapperClassName}>
        <Select
          label={typeLabel}
          dataCy={dataCy ? `${dataCy}-type` : undefined}
          value={type}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onTypeChange(e.target.value as TType)
          }
          options={typeOptions}
          bodyClassName={
            stacked ? stackedTypeSelectClassName : rowTypeSelectClassName
          }
        />
        {boundOptions && onBoundChange && (
          <Select
            label={boundLabel}
            value={bound}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onBoundChange(e.target.value as TBound)
            }
            options={boundOptions}
            bodyClassName={
              stacked ? stackedBoundSelectClassName : rowBoundSelectClassName
            }
          />
        )}
        <NumberInput
          label={valueLabel}
          dataCy={dataCy ? `${dataCy}-value` : undefined}
          value={value ?? ''}
          min={min}
          max={max}
          className={
            stacked ? stackedValueInputClassName : rowValueInputClassName
          }
          placeholder={placeholder}
          onValueChange={(v) => onValueChange(Number(v) || 0)}
        />
        <Select
          label={frequencyLabel}
          dataCy={dataCy ? `${dataCy}-frequency` : undefined}
          value={frequency}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFrequencyChange(e.target.value as TFrequency)
          }
          options={frequencyOptions}
          bodyClassName={
            stacked
              ? stackedFrequencySelectClassName
              : rowFrequencySelectClassName
          }
        />
      </div>
    </div>
  );
};

export default LimitInputRow;
